import { useRef, useState, useEffect, useCallback } from 'react';
import SignaturePad from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { Eraser } from 'lucide-react';

interface SignatureCanvasProps {
  onSave: (dataUrl: string) => void;
  onCancel: () => void;
}

export function SignatureCanvas({ onSave, onCancel }: SignatureCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const signatureRef = useRef<SignaturePad>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Keep the drawing surface sized to its container at device-pixel-ratio for
  // crisp strokes, and preserve any existing strokes across resizes so the
  // signature isn't wiped when the layout changes (e.g. mobile rotation).
  useEffect(() => {
    const container = containerRef.current;
    const pad = signatureRef.current;
    if (!container || !pad) return;

    const resize = () => {
      const canvas = pad.getCanvas();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const { width, height } = container.getBoundingClientRect();
      if (width === 0 || height === 0) return;

      // Snapshot strokes before the canvas dimensions reset clears the surface.
      const data = pad.toData();
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.getContext('2d')?.scale(ratio, ratio);
      pad.clear(); // reset internal state to match the new canvas size
      if (data.length > 0) {
        pad.fromData(data);
        setIsEmpty(false);
      } else {
        setIsEmpty(true);
      }
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const handleClear = useCallback(() => {
    signatureRef.current?.clear();
    setIsEmpty(true);
  }, []);

  const handleEnd = useCallback(() => {
    setIsEmpty(signatureRef.current?.isEmpty() ?? true);
  }, []);

  const handleSave = useCallback(() => {
    const pad = signatureRef.current;
    if (!pad || pad.isEmpty()) return;

    // Keep Retina exports below the API payload limit while preserving enough
    // resolution for PDF stamping.
    const sourceCanvas = pad.getCanvas();
    const maxWidth = 800;
    const scale = Math.min(1, maxWidth / sourceCanvas.width);
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = Math.max(1, Math.round(sourceCanvas.width * scale));
    exportCanvas.height = Math.max(1, Math.round(sourceCanvas.height * scale));
    const context = exportCanvas.getContext('2d');
    if (!context) return;
    context.drawImage(sourceCanvas, 0, 0, exportCanvas.width, exportCanvas.height);
    const dataUrl = exportCanvas.toDataURL('image/png');
    onSave(dataUrl);
  }, [onSave]);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div
        ref={containerRef}
        // min() caps the pad so header+buttons still fit on short landscape phones
        className="relative h-[min(16rem,40vh)] overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/30 bg-white sm:h-64"
      >
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex items-end justify-center pb-8">
            <div className="w-4/5 border-b border-muted-foreground/30 pb-1 text-center text-sm text-muted-foreground/60">
              Sign here
            </div>
          </div>
        )}
        <SignaturePad
          ref={signatureRef}
          penColor="#111827"
          clearOnResize={false}
          onEnd={handleEnd}
          canvasProps={{
            className: 'w-full h-full',
            style: { touchAction: 'none' },
          }}
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="hidden text-sm text-muted-foreground sm:block">
          Draw your signature above
        </p>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <Button variant="outline" className="h-11 sm:h-9" onClick={handleClear}>
            <Eraser className="h-4 w-4 mr-1" />
            Clear
          </Button>
          <Button variant="outline" className="h-11 sm:h-9" onClick={onCancel}>
            Cancel
          </Button>
          <Button className="col-span-2 h-11 sm:h-9" onClick={handleSave} disabled={isEmpty}>
            Save Signature
          </Button>
        </div>
      </div>
    </div>
  );
}
